<script lang="ts" setup>
import { ErrorStatus, TIME_OUT_TRACKING } from '~/constants'
import { useLoginForm } from '~/composables/form/use-authentication-form'
import type { UserLoginRequest } from '@kira-dancer/nuxt-common'
import { useAuthStore } from '~/stores/auth'
import { useUserEngagementEvents } from '~/composables/tracking-events/use-user-engagement-events'
import { useModalsStore } from '#lib/stores'
import VueTurnstile from 'vue-turnstile'
import { useCustomLiveChat } from '~/composables/use-custom-live-chat'

const { TURNSTILE_SITE_KEY, CAPTCHA_ENABLED } = useRuntimeConfig().public
const authStore = useAuthStore()
const { openLiveChat } = useCustomLiveChat()
const { onLogin } = useAuth()
const { setOpenAuthModal } = useModalsStore()
const { trackingProfileCompletion, trackingLogin } = useUserEngagementEvents()
const timeout = ref()
const messageErrorFormApi = ref('')
const tokenCaptcha = ref('')

const props = defineProps({
  onSuccess: {
    type: Function,
    required: false,
    default: null,
  },
})

const isLoading = ref(false)
const errorMessage = reactive<{ messages?: string[]; confirmMessage?: string }>(
  {
    messages: [],
    confirmMessage: '',
  },
)
const errorStatus = ref<ErrorStatus | null>(null)

const { meta, isSubmitting, handleSubmit, values, setFieldValue } =
  useLoginForm()

const onClearError = () => {
  errorStatus.value = null
}

watch(
  () => values.username,
  () => {
    onClearError()
  },
)

watch(
  () => values.password,
  () => {
    onClearError()
  },
)

watch(
  () => tokenCaptcha.value,
  (value) => {
    setFieldValue('captchaToken', value)
  },
)

const onClickForgotPassword = () => {
  setOpenAuthModal(false)
  authStore.setForgotPasswordStepSendMail(true)
}

const onSubmit = handleSubmit(async (request: UserLoginRequest) => {
  isLoading.value = true
  errorMessage.confirmMessage = ''
  errorMessage.messages = []
  try {
    await onLogin(request)
    setOpenAuthModal(false)
    props.onSuccess?.()
    setTimeout(() => {
      trackingProfileCompletion()
      trackingLogin()
    }, TIME_OUT_TRACKING)
  } catch (ex) {
    errorStatus.value = (ex as { status: ErrorStatus }).status
    messageErrorFormApi.value = (ex as { message: string }).message
  } finally {
    isLoading.value = false
  }
})

onBeforeUnmount(() => {
  if (timeout.value) {
    clearTimeout(timeout.value)
  }
})
</script>

<template>
  <div>
    <form
      class="form-login"
      :class="{ 'form-login-captcha': CAPTCHA_ENABLED }"
      action="#"
      @submit.prevent="onSubmit"
    >
      <FormInput
        class="input-username"
        name="username"
        placeholder="Nhập tên đăng nhập của bạn"
        label="Tên đăng nhập"
        :max-length="29"
      />
      <FormPassword
        class="input-password"
        name="password"
        placeholder="Nhập mật khẩu của bạn"
        label="Mật khẩu"
        :max-length="32"
      />
      <div class="flex justify-end mt-[16px]">
        <span
          class="form-login__forgot-pwd"
          @click.prevent="onClickForgotPassword"
          >Quên mật khẩu?</span
        >
      </div>
      <div v-if="errorStatus" class="error-container">
        <BaseErrorMessage
          v-if="
            [
              ErrorStatus.UserNotFoundError,
              ErrorStatus.InvalidLoginCredentialError,
            ].includes(errorStatus)
          "
          :message="`Sai tên đăng nhập hoặc mật khẩu. Vui lòng đăng nhập lại!`"
        />
        <BaseErrorMessage
          v-else-if="errorStatus === ErrorStatus.LoginFailedManyTimesError"
        >
          <template #message>
            <span
              >Quý khách đã đăng nhập sai quá nhiều lần. Vui lòng
              <span class="text-underline" @click.prevent="openLiveChat()"
                >liên hệ CSKH.</span
              ></span
            >
          </template>
        </BaseErrorMessage>
        <BaseErrorMessage
          v-else-if="
            errorStatus === ErrorStatus.LockedAccount ||
            errorStatus === ErrorStatus.CaptchaVerificationError
          "
          :message="messageErrorFormApi"
        />
        <BaseErrorMessage v-else>
          <template #message>
            <span
              >Đã có lỗi xảy ra. Vui lòng
              <span class="text-underline" @click.prevent="openLiveChat()"
                >liên hệ CSKH.</span
              ></span
            >
          </template>
        </BaseErrorMessage>
      </div>
      <BaseButton
        custom-class="form-login__submit-btn"
        type="submit"
        variant="primary"
        button-id="submit-login"
        button-name="submit-login"
        :disabled="!meta.dirty || !meta.valid || isLoading || isSubmitting"
        :loading="isLoading || isSubmitting"
        label="Đăng nhập ngay"
      />
      <div v-if="CAPTCHA_ENABLED" class="modal-captcha">
        <VueTurnstile
          v-model="tokenCaptcha"
          :site-key="TURNSTILE_SITE_KEY"
          size="flexible"
          theme="dark"
        />
        <div class="hidden">
          <FormInput name="captchaToken" />
        </div>
      </div>
    </form>
    <div class="banner-login">
      <img src="/images/auth/banner-login-mb.webp" alt="banner mobile" />
    </div>
  </div>
</template>
<style
  lang="scss"
  src="/assets/styles/components/desktop/form/login-form.scss"
  scoped
></style>
